import { graphqlRequest } from "../graphqlClient";
import type {
	ScenarioAccessViewConnection,
	ScenarioAccessViewFilterInput,
} from "../models/scenarioAccessView";
import type {
	ScenarioViewConnection,
	ScenarioViewFilterInput,
} from "../models/scenarioView";

export interface ScenarioViewSortInput {
	field: string;
	order: "Ascending" | "Descending";
}

interface ScenarioViewListResponse {
	scenarios: {
		scenarioViewList: ScenarioViewConnection;
	};
}

const SCENARIO_VIEW_NODE_FIELDS = `
  id
  name
  description
  status
  priority
  label
  category
  createdByUserId
  createdByUserName
  modifiedByUserId
  currentVersion
  commentCount
  parkedAt
  parkedBy
  parkedByName
  parkedReason
  reviewDate
  minCash
  coverage
  netCashImpact
  adjustmentCount
  refreshStatus
  freshness
  canView
  canComment
  canEdit
  isOwner
  isShared
  isInherited
  isFavorite
  dateCreated
  dateModified
`;

export async function getScenariosGraphql(
	filter?: ScenarioViewFilterInput,
	first?: number,
	after?: string,
	sort?: ScenarioViewSortInput[],
): Promise<ScenarioViewConnection> {
	const query = `
    query ScenarioViewList($filter: ScenarioViewFilterInput, $first: Int, $after: String, $sort: [ScenarioViewSortSortByInput!]) {
      scenarios {
        scenarioViewList(filter: $filter, first: $first, after: $after, sort: $sort) {
          edges {
            cursor
            node {${SCENARIO_VIEW_NODE_FIELDS}}
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
        }
      }
    }
  `;
	const data = await graphqlRequest<ScenarioViewListResponse>(query, {
		filter,
		first,
		after,
		sort,
	});
	return data.scenarios.scenarioViewList;
}

interface ScenarioViewCountResponse {
	scenarios: {
		scenarioViewCount: number;
	};
}

export async function getScenariosCount(
	filter?: ScenarioViewFilterInput,
): Promise<number> {
	const query = `
    query ScenarioViewCount($filter: ScenarioViewFilterInput) {
      scenarios {
        scenarioViewCount(filter: $filter)
      }
    }
  `;
	const data = await graphqlRequest<ScenarioViewCountResponse>(query, {
		filter,
	});
	return data.scenarios.scenarioViewCount;
}

const SCENARIO_ACCESS_VIEW_NODE_FIELDS = `
  scenarioId
  userId
  userName
  isOwner
  canView
  canComment
  canEdit
  isExplicitShare
  isInherited
`;

interface ScenarioAccessViewListResponse {
	scenarios: {
		scenarioAccessViewList: ScenarioAccessViewConnection;
	};
}

/**
 * Fetch every user who can access a scenario (owner, explicit shares and users
 * inheriting status-conditional access), sourced from vw_ScenarioAccess. The
 * server gates this by scenario access, so a caller without access receives an
 * empty connection.
 */
export async function getScenarioAccessGraphql(
	scenarioId: number,
	first = 500,
): Promise<ScenarioAccessViewConnection> {
	const filter: ScenarioAccessViewFilterInput = { scenarioId: { eq: scenarioId } };
	const query = `
    query ScenarioAccessViewList($filter: ScenarioAccessViewFilterInput, $first: Int) {
      scenarios {
        scenarioAccessViewList(filter: $filter, first: $first) {
          edges {
            cursor
            node {${SCENARIO_ACCESS_VIEW_NODE_FIELDS}}
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
        }
      }
    }
  `;
	const data = await graphqlRequest<ScenarioAccessViewListResponse>(query, {
		filter,
		first,
	});
	return data.scenarios.scenarioAccessViewList;
}

/**
 * Count of scenarios the current user can see whose derived freshness is "Updating".
 * Drives the scenario refresh banner — polled only while the count is > 0 and only on
 * the Scenarios / Scenario detail pages.
 */
export async function getUpdatingScenariosCount(): Promise<number> {
	return getScenariosCount({ freshness: { eq: "Updating" } });
}
